import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetJobCardQuery, useSetMaterialOrderQuantityMutation, useAddCustomMaterialLineMutation,
  type JobCardMaterialLine,
} from '@/redux/internal-services/jobsApi';
import {
  useSendEmailMutation,
  useUploadCommunicationAttachmentMutation,
} from '@/redux/internal-services/workspaceApiExtensions';

// ── Export helpers ────────────────────────────────────────────────────────────

/** Quantities come from unit conversions (e.g. 110.00000000000001) — show at most 2 decimals. */
const fmtQty = (n: number): string => String(Number(Number(n).toFixed(2)));

const EXPORT_COLS = ['Description', 'Item Code', 'Supplier Product Code', 'Qty', 'UOM', 'Notes'] as const;

function lineToRow(l: JobCardMaterialLine, qtys: Record<string, number>): string[] {
  return [
    l.description ?? '',
    l.item_code ?? '',
    l.supplier_product_code ?? '',
    fmtQty(qtys[l.estimate_line_id] ?? l.order_quantity ?? l.quantity ?? 1),
    l.uom ?? '',
    l.notes ?? '',
  ];
}

function buildCsv(lines: JobCardMaterialLine[], qtys: Record<string, number>): string {
  const rows = [EXPORT_COLS as unknown as string[], ...lines.map((l) => lineToRow(l, qtys))];
  return rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
}

function buildXls(lines: JobCardMaterialLine[], qtys: Record<string, number>, supplierName: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const headerRow = `<tr>${EXPORT_COLS.map((h) => `<th>${esc(h)}</th>`).join('')}</tr>`;
  const dataRows = lines.map((l) => `<tr>${lineToRow(l, qtys).map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`).join('');
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"/><title>${esc(supplierName)} Order</title></head><body><table>${headerRow}${dataRows}</table></body></html>`;
}

function printAsPdf(lines: JobCardMaterialLine[], qtys: Record<string, number>, supplierName: string) {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const headerRow = EXPORT_COLS.map((h) => `<th style="text-align:left;padding:6px 10px;background:#f3f0ff;border-bottom:2px solid #7c3aed;font-size:11px">${esc(h)}</th>`).join('');
  const dataRows = lines.map((l, i) =>
    `<tr style="background:${i % 2 === 0 ? '#fff' : '#faf9ff'}">${lineToRow(l, qtys).map((c) => `<td style="padding:5px 10px;font-size:11px;border-bottom:1px solid #e4e4e7">${esc(c)}</td>`).join('')}</tr>`
  ).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${esc(supplierName)} Order</title><style>body{font-family:system-ui,sans-serif;padding:24px}h2{font-size:14px;margin-bottom:4px}p{font-size:11px;color:#71717a;margin:0 0 16px}table{width:100%;border-collapse:collapse}@media print{@page{margin:24px}}</style></head><body><h2>Material Order – ${esc(supplierName)}</h2><p>${lines.length} line${lines.length !== 1 ? 's' : ''}</p><table><thead><tr>${headerRow}</tr></thead><tbody>${dataRows}</tbody></table></body></html>`;
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '-9999px';
  iframe.style.left = '-9999px';
  document.body.appendChild(iframe);
  iframe.contentDocument!.write(html);
  iframe.contentDocument!.close();
  iframe.contentWindow!.focus();
  setTimeout(() => {
    iframe.contentWindow!.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 250);
}

function triggerDownload(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Export format picker ──────────────────────────────────────────────────────

const ExportMenu: React.FC<{
  supplierName: string;
  lines: JobCardMaterialLine[];
  qtys: Record<string, number>;
  onClose: () => void;
}> = ({ supplierName, lines, qtys, onClose }) => {
  const slug = supplierName.replace(/[^a-z0-9]/gi, '_');
  const doExport = (fmt: 'csv' | 'excel' | 'pdf') => {
    if (fmt === 'csv') {
      triggerDownload(buildCsv(lines, qtys), `${slug}_order.csv`, 'text/csv');
    } else if (fmt === 'excel') {
      triggerDownload(buildXls(lines, qtys, supplierName), `${slug}_order.xls`, 'application/vnd.ms-excel');
    } else {
      printAsPdf(lines, qtys, supplierName);
    }
    onClose();
  };
  const options: { fmt: 'csv' | 'excel' | 'pdf'; icon: string; label: string }[] = [
    { fmt: 'csv',   icon: '📄', label: 'CSV (.csv)' },
    { fmt: 'excel', icon: '📊', label: 'Excel (.xls)' },
    { fmt: 'pdf',   icon: '🖨️',  label: 'PDF (print)' },
  ];
  return (
    <>
      <div className="fixed inset-0 z-[85]" onClick={onClose} />
      <div className="absolute right-0 top-full z-[86] mt-1 w-36 rounded-xl border border-ui-border bg-ui-card shadow-lg overflow-hidden">
        {options.map(({ fmt, icon, label }) => (
          <button
            key={fmt}
            type="button"
            onClick={() => doExport(fmt)}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs text-ui-text hover:bg-ui-hover"
          >
            <span>{icon}</span>
            {label}
          </button>
        ))}
      </div>
    </>
  );
};

// ── Send to supplier popup ────────────────────────────────────────────────────

const SupplierEmailModal: React.FC<{
  supplierName: string;
  lines: JobCardMaterialLine[];
  qtys: Record<string, number>;
  onClose: () => void;
}> = ({ supplierName, lines, qtys, onClose }) => {
  const [sendEmail, { isLoading: isSending }] = useSendEmailMutation();
  const [uploadAttachment, { isLoading: isUploading }] = useUploadCommunicationAttachmentMutation();

  const defaultSubject = `Material Order Request – ${supplierName}`;
  const defaultBody = [
    `Hi ${supplierName} team,`,
    '',
    'Please find attached our material order request. Details are as follows:',
    '',
    ...lines.map((l) => {
      const qty = qtys[l.estimate_line_id] ?? l.order_quantity ?? l.quantity ?? 1;
      const code = l.product_code || l.supplier_product_code || l.item_code || '';
      return `• ${l.description ?? 'Item'}${code ? ` (${code})` : ''} — ${fmtQty(qty)} ${l.uom ?? ''}`.trimEnd();
    }),
    '',
    'The full list is attached as a CSV file. Please confirm availability and expected delivery.',
    '',
    'Thank you.',
  ].join('\n');

  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    setError('');
    if (!toEmail.trim()) { setError('Recipient email is required.'); return; }
    const csv = buildCsv(lines, qtys);
    const csvFile = new File([csv], `${supplierName.replace(/[^a-z0-9]/gi, '_')}_order.csv`, { type: 'text/csv' });
    try {
      const attachment = await uploadAttachment(csvFile).unwrap();
      await sendEmail({
        recipients: [{ recipient_type: 'external', email: toEmail.trim(), name: supplierName }],
        subject,
        body,
        attachments: [attachment],
      }).unwrap();
      setSent(true);
      setTimeout(onClose, 2000);
    } catch {
      setError('Failed to send email — please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative z-10 w-full max-w-lg rounded-2xl bg-ui-card shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ui-border px-5 py-4 shrink-0">
          <div>
            <p className="text-sm font-semibold text-ui-text">Send to supplier</p>
            <p className="text-[11px] text-ui-text3 mt-0.5">{supplierName} · {lines.length} line{lines.length !== 1 ? 's' : ''} · CSV attached</p>
          </div>
          <button type="button" onClick={onClose} className="text-ui-text3 hover:text-ui-text ml-3 shrink-0">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {sent ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <p className="text-sm font-semibold text-ui-text">Email sent</p>
            <p className="mt-1 text-xs text-ui-text3">Your order request was sent to {toEmail}.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
              {/* To */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ui-text">To (supplier email)</label>
                <input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="supplier@example.com"
                  className="w-full rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text placeholder-gray-400 focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark/40"
                  autoFocus
                />
              </div>

              {/* Subject */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ui-text">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark/40"
                />
              </div>

              {/* Body */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ui-text">Message</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className="w-full rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark/40 resize-none font-mono leading-relaxed"
                />
              </div>

              {/* Attachment badge */}
              <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-subtle px-3 py-2">
                <svg className="h-4 w-4 shrink-0 text-ui-text3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span className="text-xs text-ui-text2">{supplierName.replace(/[^a-z0-9]/gi, '_')}_order.csv</span>
                <span className="ml-auto text-[10px] text-ui-text3">auto-attached</span>
              </div>

              {error && <p className="text-xs text-[#CF4148]">{error}</p>}
            </div>

            <div className="flex gap-3 border-t border-ui-border px-5 py-4 shrink-0">
              <button
                type="button"
                onClick={onClose}
                disabled={isSending || isUploading}
                className="flex-1 rounded-xl border border-ui-border py-2.5 text-sm font-medium text-ui-text2 hover:bg-ui-hover disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={isSending || isUploading}
                className="flex-1 rounded-xl bg-brand-dark py-2.5 text-sm font-semibold text-white disabled:opacity-60 hover:brightness-110"
              >
                {(isSending || isUploading) ? 'Sending…' : 'Send email'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const MaterialListModal: React.FC<{ engagementId: string; onClose: () => void }> = ({ engagementId, onClose }) => {
  const { t } = useTranslation();
  const { data: card, isLoading } = useGetJobCardQuery(engagementId);
  const [setOrderQty, { isLoading: isSavingQty }] = useSetMaterialOrderQuantityMutation();
  const [addCustomLine, { isLoading: isAdding }] = useAddCustomMaterialLineMutation();
  const [groupBy, setGroupBy] = useState<'supplier' | 'item'>('supplier');
  const [emailSupplier, setEmailSupplier] = useState<{ key: string; lines: JobCardMaterialLine[] } | null>(null);
  const [exportMenu, setExportMenu] = useState<{ key: string; lines: JobCardMaterialLine[] } | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [newSupplier, setNewSupplier] = useState('');
  const [newQty, setNewQty] = useState('');
  const [newUom, setNewUom] = useState('');
  const [saveError, setSaveError] = useState('');

  // Local quantity state — edited locally, committed on Save changes
  const [localQtys, setLocalQtys] = useState<Record<string, number>>({});

  const allLines: JobCardMaterialLine[] = useMemo(
    () => Object.values(card?.materials_by_supplier ?? {}).flat(),
    [card],
  );

  // Seed localQtys from server data on first load or when card changes
  useEffect(() => {
    const initial: Record<string, number> = {};
    for (const line of allLines) {
      initial[line.estimate_line_id] = line.order_quantity ?? line.quantity ?? 1;
    }
    setLocalQtys(initial);
  }, [allLines]);

  const bySupplier = card?.materials_by_supplier ?? {};
  const byItem = useMemo(() => {
    const groups: Record<string, JobCardMaterialLine[]> = {};
    for (const line of allLines) {
      const key = line.description || line.item_code || 'Other';
      (groups[key] ??= []).push(line);
    }
    return groups;
  }, [allLines]);

  const groups = groupBy === 'supplier' ? bySupplier : byItem;
  const totalLines = allLines.length;
  const mlId = engagementId.slice(-4).toUpperCase();

  const adjustQty = (lineId: string, delta: number) => {
    setLocalQtys((prev) => ({
      ...prev,
      [lineId]: Math.max(1, (prev[lineId] ?? 1) + delta),
    }));
  };

  const handleSave = async () => {
    setSaveError('');
    try {
      // Find lines where quantity changed
      const changed = allLines.filter(
        (l) => localQtys[l.estimate_line_id] !== (l.order_quantity ?? l.quantity),
      );
      await Promise.all(
        changed.map((l) =>
          setOrderQty({
            engagementId,
            estimateLineId: l.estimate_line_id,
            order_quantity: localQtys[l.estimate_line_id] ?? null,
          }).unwrap(),
        ),
      );
      onClose();
    } catch {
      setSaveError(t('jobs.saveFailed', 'Could not save changes — try again.'));
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDesc.trim()) return;
    await addCustomLine({
      engagementId, description: newDesc.trim(), supplier_code: newSupplier || undefined,
      order_quantity: newQty ? Number(newQty) : undefined, uom: newUom || undefined,
    }).unwrap();
    setNewDesc(''); setNewSupplier(''); setNewQty(''); setNewUom(''); setShowAdd(false);
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-[540px] max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl bg-ui-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-1 shrink-0">
          <div>
            <p className="text-[15px] font-bold text-ui-text">
              ML-{mlId} · <span className="font-normal text-ui-text3">draft</span>
            </p>
            <p className="text-[11px] text-ui-text3 mt-0.5">
              {t('jobs.materialListSubtitle', 'Generated from the accepted scope and the supplier catalogue')}
              {totalLines > 0 && ` · ${totalLines} ${totalLines === 1 ? 'line' : 'lines'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ui-text3 hover:bg-ui-hover hover:text-ui-text"
            aria-label={t('common.close', 'Close')}
          >
            ✕
          </button>
        </div>

        {/* Toggle */}
        <div className="flex gap-2 px-4 pt-2 pb-1 shrink-0">
          <button
            type="button"
            onClick={() => setGroupBy('supplier')}
            className={`rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors ${
              groupBy === 'supplier'
                ? 'bg-brand-dark text-white'
                : 'border border-ui-border text-ui-text2 hover:bg-ui-hover'
            }`}
          >
            {t('jobs.groupBySupplier', 'Group by supplier')}
          </button>
          <button
            type="button"
            onClick={() => setGroupBy('item')}
            className={`rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors ${
              groupBy === 'item'
                ? 'bg-brand-dark text-white'
                : 'border border-ui-border text-ui-text2 hover:bg-ui-hover'
            }`}
          >
            {t('jobs.allItems', 'All items')}
          </button>
        </div>

        {/* Line items */}
        <div className="flex-1 overflow-y-auto px-4 pb-2 min-h-0">
          {isLoading && (
            <p className="py-4 text-center text-xs text-ui-text3">{t('common.loading', 'Loading…')}</p>
          )}
          {!isLoading && Object.keys(groups).length === 0 && (
            <p className="py-4 text-center text-xs text-ui-text3">{t('jobs.noMaterials', 'No material lines yet.')}</p>
          )}

          <div className="space-y-5">
            {Object.entries(groups).map(([groupKey, lines]) => (
              <div key={groupKey}>
                {/* Supplier / group header */}
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-ui-text truncate">{groupKey}</p>
                    <p className="text-[11px] text-ui-text3">
                      {lines.length} {lines.length === 1 ? 'line' : 'lines'}
                    </p>
                  </div>
                  <div className="relative flex shrink-0 gap-1.5">
                    <button
                      type="button"
                      className="rounded-md border border-ui-border px-2 py-1 text-[11px] font-medium text-ui-text2 hover:bg-ui-hover"
                      onClick={() => setExportMenu((prev) => prev?.key === groupKey ? null : { key: groupKey, lines })}
                    >
                      ↑ {t('jobs.export', 'Export')} ▾
                    </button>
                    {exportMenu?.key === groupKey && (
                      <ExportMenu
                        supplierName={groupKey}
                        lines={exportMenu.lines}
                        qtys={localQtys}
                        onClose={() => setExportMenu(null)}
                      />
                    )}
                    <button
                      type="button"
                      className="rounded-md border border-brand-dark/40 px-2 py-1 text-[11px] font-medium text-brand-dark hover:bg-brand-light/20"
                      onClick={() => setEmailSupplier({ key: groupKey, lines })}
                    >
                      {t('jobs.sendToSupplier', 'Send to supplier')}
                    </button>
                  </div>
                </div>

                {/* Line items */}
                <div className="divide-y divide-ui-border rounded-xl border border-ui-border overflow-hidden">
                  {lines.map((line) => (
                    <div key={line.estimate_line_id} className="flex items-center gap-2.5 px-3 py-2.5 bg-ui-card hover:bg-ui-subtle/50">
                      <input
                        type="checkbox"
                        className="shrink-0 rounded border-ui-border accent-brand-dark"
                        aria-label={line.description ?? line.item_code ?? ''}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-ui-text leading-tight">
                          {line.description || line.item_code || t('jobs.unknownItem', 'Unknown item')}
                          {line.is_custom && (
                            <span className="ml-1.5 rounded bg-brand-light/30 px-1.5 py-0.5 text-[9px] font-semibold text-brand-dark align-middle">
                              custom
                            </span>
                          )}
                        </p>
                        {(line.product_code || line.uom) && (
                          <p className="text-[11px] text-ui-text3 leading-tight mt-0.5">
                            {[line.product_code, line.uom].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>

                      {/* Quantity controls */}
                      <div className="flex items-center gap-0 shrink-0 rounded-lg border border-ui-border overflow-hidden">
                        <button
                          type="button"
                          onClick={() => adjustQty(line.estimate_line_id, -1)}
                          className="flex h-7 w-7 items-center justify-center text-ui-text2 hover:bg-ui-hover text-lg leading-none"
                          aria-label="Decrease"
                        >
                          −
                        </button>
                        <span className="min-w-[52px] text-center text-[12px] font-medium text-ui-text px-1 leading-7">
                          {fmtQty(localQtys[line.estimate_line_id] ?? (line.order_quantity ?? line.quantity ?? 1))}{' '}
                          <span className="text-[11px] font-normal text-ui-text3">{line.uom ?? ''}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => adjustQty(line.estimate_line_id, 1)}
                          className="flex h-7 w-7 items-center justify-center text-ui-text2 hover:bg-ui-hover text-lg leading-none"
                          aria-label="Increase"
                        >
                          +
                        </button>
                      </div>

                      {/* Restricted badge */}
                      <span className="shrink-0 rounded border border-ui-border px-1.5 py-0.5 text-[10px] text-ui-text3">
                        {t('jobs.restricted', 'Restricted')}
                      </span>

                      {/* Remove button (custom lines only) */}
                      {line.is_custom ? (
                        <button
                          type="button"
                          className="shrink-0 flex h-5 w-5 items-center justify-center rounded text-ui-text3 hover:text-ui-text hover:bg-ui-hover text-[11px]"
                          aria-label={t('common.remove', 'Remove')}
                        >
                          ×
                        </button>
                      ) : (
                        <span className="w-5 shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Add custom line form */}
          {showAdd && (
            <form onSubmit={(e) => void handleAdd(e)} className="mt-3 rounded-xl border border-ui-border bg-ui-subtle p-3 space-y-2">
              <input
                value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                placeholder={t('jobs.description', 'Description')}
                className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-sm text-ui-text focus:outline-none focus:border-brand-dark"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  value={newSupplier} onChange={(e) => setNewSupplier(e.target.value)}
                  placeholder={t('jobs.supplierCode', 'Supplier')}
                  className="rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-sm text-ui-text focus:outline-none focus:border-brand-dark"
                />
                <input
                  type="number" value={newQty} onChange={(e) => setNewQty(e.target.value)}
                  placeholder={t('jobs.orderQuantity', 'Qty')}
                  className="rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-sm text-ui-text focus:outline-none focus:border-brand-dark"
                />
                <input
                  value={newUom} onChange={(e) => setNewUom(e.target.value)}
                  placeholder={t('jobs.uom', 'UOM')}
                  className="rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-sm text-ui-text focus:outline-none focus:border-brand-dark"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button" onClick={() => setShowAdd(false)}
                  className="rounded-lg px-3 py-1.5 text-xs text-ui-text2 hover:bg-ui-hover"
                >
                  {t('workflow.cancel', 'Cancel')}
                </button>
                <button
                  type="submit" disabled={isAdding || !newDesc.trim()}
                  className="rounded-lg bg-brand-dark px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 hover:opacity-90"
                >
                  {t('jobs.addLine', 'Add')}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Add line item */}
        {!showAdd && (
          <div className="px-4 py-2 shrink-0 border-t border-ui-border">
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="text-[13px] font-medium text-brand-dark hover:opacity-80"
            >
              + {t('jobs.addLineItem', 'Add line item')}
            </button>
          </div>
        )}

        {/* Save / Cancel */}
        <div className="px-4 pt-2 pb-3 shrink-0 space-y-2">
          {saveError && <p className="text-[11px] text-[#CF4148]">{saveError}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSavingQty}
              className="flex-1 rounded-xl bg-brand-dark py-3 text-sm font-semibold text-white disabled:opacity-60 hover:opacity-90"
            >
              {isSavingQty ? t('common.saving', 'Saving…') : t('common.saveChanges', 'Save changes')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border border-ui-border py-3 text-sm font-medium text-ui-text2 hover:bg-ui-hover"
            >
              {t('workflow.cancel', 'Cancel')}
            </button>
          </div>
          <p className="text-[10px] text-ui-text3 leading-relaxed">
            {t('jobs.materialListFooterNote', 'Saving keeps the draft against the property; each supplier\'s request is sent from its own group above. Quantities carry the 10% waste allowance from the accepted scope; edits here do not change the proposal.')}
          </p>
        </div>
      </div>
    </div>

    {emailSupplier && (
      <SupplierEmailModal
        supplierName={emailSupplier.key}
        lines={emailSupplier.lines}
        qtys={localQtys}
        onClose={() => setEmailSupplier(null)}
      />
    )}
  </>
  );
};

export default MaterialListModal;
